import { useEffect, useMemo, useState } from 'react';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, useTableSort } from '../../components/SortableTh';
import {
  Callout,
  DateInput,
  ListEmptyState,
  LoadingSpinner,
  Select,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { ShipmentByPortVesselReport } from '../../types/reports';
import { formatMoney } from '../../utils/format';
import { ReportExportButtons } from './ReportExportButtons';
import { exportShipmentByPortVessel } from './exportShipmentReports';

export function ShipmentByPortVesselPage() {
  const labels = t().reports;
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [groupBy, setGroupBy] = useState<'port' | 'vessel'>('port');
  const [report, setReport] = useState<ShipmentByPortVesselReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sort, toggleSort } = useTableSort('label');

  const rows = useMemo(
    () =>
      report
        ? sortItems(report.data, sort, {
            label: (r) => r.label,
            fileCount: (r) => r.fileCount,
            gross: (r) => r.expenses.gross,
          })
        : [],
    [report, sort],
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getShipmentByPortVesselReport({
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          groupBy,
        });
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, groupBy]);

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <ReportExportButtons
            disabled={!report || loading}
            onExcel={() => {
              if (!report) return;
              exportShipmentByPortVessel({ report, format: 'excel' });
            }}
            onPdf={() => {
              if (!report) return;
              exportShipmentByPortVessel({ report, format: 'pdf' });
            }}
          />
        }
      >
        <FilterField label={labels.dateFrom}>
          <DateInput
            value={dateFrom}
            showClearButton
            showQuickFill={false}
            showFormatHint={false}
            placeholder={labels.dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.dateTo}>
          <DateInput
            value={dateTo}
            showClearButton
            showQuickFill={false}
            showFormatHint={false}
            placeholder={labels.dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.groupBy}>
          <Select
            value={groupBy}
            size="sm"
            showClearButton={false}
            options={[
              { value: 'port', label: labels.groupByPort },
              { value: 'vessel', label: labels.groupByVessel },
            ]}
            onChange={(e) =>
              setGroupBy(e.target.value === 'vessel' ? 'vessel' : 'port')
            }
          />
        </FilterField>
      </ListFiltersBar>
      {error ? <Callout variant="danger">{error}</Callout> : null}
      {loading ? (
        <LoadingSpinner message={t().common.loading} />
      ) : !report || rows.length === 0 ? (
        <ListEmptyState title={t().common.empty} />
      ) : (
        <DataTablePanel className="min-h-0 flex-1">
          <table className="min-w-full text-left text-sm">
            <thead className={dataTableHeadClass}>
              <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                <SortableTh sortKey="label" sort={sort} onSort={toggleSort}>
                  {groupBy === 'vessel' ? labels.vessel : labels.port}
                </SortableTh>
                <SortableTh sortKey="fileCount" sort={sort} onSort={toggleSort}>
                  {labels.fileCount}
                </SortableTh>
                <SortableTh
                  sortKey="gross"
                  sort={sort}
                  onSort={toggleSort}
                  className="text-right"
                >
                  {labels.expenseGross}
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key} className="border-b border-line/70">
                  <td className="px-4 py-3 font-medium">{row.label}</td>
                  <td className="px-4 py-3 tabular-nums">{row.fileCount}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {formatMoney(row.expenses.gross)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTablePanel>
      )}
    </PageShell>
  );
}
